'use client';

import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { NeighborhoodMiniMap } from '@/src/components/features/property/NeighborhoodModule/shared/NeighborhoodMiniMap';
import { useCommutePreferences, useSetCommutePreferences } from '@/src/lib/hooks/neighborhood';
import { CommuteTimeCards } from '@/src/components/features/property/NeighborhoodModule/shared/CommuteTimeCards';
import { calculateCommuteTimes } from '@/src/components/features/property/NeighborhoodModule/shared/routeCalculation';
import type { CommuteTime, TravelMode } from '@/src/components/features/property/NeighborhoodModule/shared/types';
import { useEffect, useState } from 'react';
import {
  fallbackContainer,
  fallbackIcon,
  fallbackSubtext,
  fallbackText,
  mapContainer,
  mapSection,
  moduleContainer,
} from './neighborhoodModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

const isValidCoordinate = (lat?: number, lon?: number): boolean =>
  typeof lat === 'number' &&
  typeof lon === 'number' &&
  isFinite(lat) &&
  isFinite(lon) &&
  lat >= -90 &&
  lat <= 90 &&
  lon >= -180 &&
  lon <= 180 &&
  !(lat === 0 && lon === 0);

/**
 * NeighborhoodContent — map preview and commute cards for the HALO neighborhood module.
 *
 * Ported from legacy HALO NeighborhoodContent. Redux lookup replaced with
 * `listing` prop. Legacy DynamicMap (Google Maps + POI + StreetView) is not
 * yet ported to vercel — we reuse the vercel NeighborhoodMiniMap which
 * already provides a static Google map with commute polyline. POI pills and
 * StreetView toggle live in the spoke, not the summary card.
 */
export const NeighborhoodContent = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const commutePrefs = useCommutePreferences();
  const setCommutePrefs = useSetCommutePreferences();
  const savedDestination = commutePrefs.destination;
  const travelMode = commutePrefs.travelMode as TravelMode | undefined;

  const geo = listing.geo;
  const lat = geo?.lat;
  const lon = geo?.lon;
  const hasValidCoordinates = isValidCoordinate(lat, lon);

  const address = listing.address;
  const displayAddressParts: Array<string> = [];
  if (address) {
    if (!address.hideStreet && address.street) displayAddressParts.push(address.street);
    if (address.city) displayAddressParts.push(address.city);
    if (address.state) displayAddressParts.push(address.state);
  }
  const displayAddress = displayAddressParts.join(', ');

  const [commuteTimes, setCommuteTimes] = useState<Array<CommuteTime>>([]);

  useEffect(() => {
    if (!savedDestination || !hasValidCoordinates) {
      setCommuteTimes([]);
      return;
    }
    let cancelled = false;
    void calculateCommuteTimes({ lat: lat!, lng: lon! }, savedDestination)
      .then((times) => {
        if (!cancelled) setCommuteTimes(times);
        return undefined;
      })
      .catch(() => {
        if (!cancelled) setCommuteTimes([]);
        return undefined;
      });
    return () => {
      cancelled = true;
    };
  }, [savedDestination, hasValidCoordinates, lat, lon]);

  return (
    <section className={moduleContainer} data-testid="neighborhood-module" aria-label={t('neighborhoodInfo')}>
      <div className={mapSection}>
        <div className={mapContainer}>
          {hasValidCoordinates ? (
            <NeighborhoodMiniMap
              lat={lat!}
              lng={lon!}
              destination={savedDestination}
              travelMode={travelMode ?? 'DRIVING'}
            />
          ) : (
            <div className={fallbackContainer} aria-label={t('locationNotAvailableAria')}>
              <div className={fallbackIcon} aria-hidden="true">
                📍
              </div>
              <div className={fallbackText}>{t('locationNotAvailable')}</div>
              <div className={fallbackSubtext}>
                {displayAddress ? t('addressFormat', { address: displayAddress }) : t('noAddressInfo')}
              </div>
            </div>
          )}
        </div>

        {savedDestination && hasValidCoordinates && commuteTimes.length > 0 && (
          <CommuteTimeCards
            destination={savedDestination}
            commuteTimes={commuteTimes}
            selectedMode={travelMode}
            onModeSelect={(mode) => setCommutePrefs({ ...commutePrefs, travelMode: mode })}
            onEdit={() => setCommutePrefs({ destination: null, travelMode: 'DRIVING' })}
          />
        )}
      </div>
    </section>
  );
};
